import { Users, ClipboardList, BarChart3, UserCog, PackageCheck, Factory, Landmark, Brain, Moon, Sun, LogOut, Loader2, ShieldAlert } from "lucide-react";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AuthPage } from "./features/auth/AuthPage";
import { useSession } from "./features/auth/useSession";
import { useThemeStore } from "./store/theme";
import { supabase } from "./lib/supabase";
import { adminApi } from "./lib/api";
import { VendorsPage } from "./features/admin/VendorsPage";
import { CasesPage } from "./features/admin/CasesPage";
import { AccountsPage } from "./features/admin/AccountsPage";
import { OrdersPage } from "./features/admin/OrdersPage";
import { MakersPage } from "./features/admin/MakersPage";
import { PayoutsPage } from "./features/admin/PayoutsPage";
import { DashboardPage } from "./features/admin/DashboardPage";
import { AiLearningPage } from "./features/admin/AiLearningPage";

type Tab = "dash" | "vendors" | "cases" | "orders" | "makers" | "payouts" | "accounts" | "ai";

const NAV_ITEMS: Array<{ k: Tab; t: string; icon: React.ReactNode }> = [
  { k: "dash", t: "ダッシュボード", icon: <BarChart3 size={18} /> },
  { k: "cases", t: "案件管理", icon: <ClipboardList size={18} /> },
  { k: "orders", t: "発注", icon: <PackageCheck size={18} /> },
  { k: "makers", t: "メーカー", icon: <Factory size={18} /> },
  { k: "payouts", t: "支払管理", icon: <Landmark size={18} /> },
  { k: "vendors", t: "業者管理", icon: <Users size={18} /> },
  { k: "accounts", t: "アカウント", icon: <UserCog size={18} /> },
  { k: "ai", t: "AI学習", icon: <Brain size={18} /> },
];

const A_TAB_KEY = "sitame_a_tab";
const A_TABS: Tab[] = ["dash", "vendors", "cases", "orders", "makers", "payouts", "accounts", "ai"];

/** タブ → URLパス。各画面を https://admin.sita-me.com/xx に分ける。dashはルート。 */
function aPathFor(tab: Tab): string { return tab === "dash" ? "/" : `/${tab}`; }
/** 現在のURLパス → タブ。 */
function aParsePath(): Tab {
  const seg = window.location.pathname.replace(/^\/+/, "").split("/")[0] as Tab;
  return A_TABS.includes(seg) ? seg : "dash";
}
/** 初期状態：URLを優先。ルート("/")のときのみ localStorage から前回タブを復元。 */
function aInitialTab(): Tab {
  if (window.location.pathname !== "/") return aParsePath();
  try { const t = localStorage.getItem(A_TAB_KEY) as Tab | null; if (t && A_TABS.includes(t)) return t; } catch { /* noop */ }
  return "dash";
}

export function App() {
  const { session, loading } = useSession();
  const [tab, setTab] = useState<Tab>(aInitialTab);
  const mode = useThemeStore((s) => s.mode);
  const setMode = useThemeStore((s) => s.setMode);

  // 画面状態（タブ）をURL(パス)へ同期：戻る/更新/共有に対応。localStorageは補助。
  useEffect(() => {
    try { localStorage.setItem(A_TAB_KEY, tab); } catch { /* noop */ }
    const target = aPathFor(tab);
    if (window.location.pathname !== target) window.history.pushState({}, "", target);
  }, [tab]);

  // ブラウザの戻る/進む → URLからタブを復元
  useEffect(() => {
    const onPop = () => setTab(aParsePath());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const { data: role, isLoading: roleLoading } = useQuery({
    queryKey: ["my-role", session?.user.id],
    queryFn: () => adminApi.myRole(),
    enabled: !!session,
  });

  if (loading || (session && roleLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)] text-[var(--color-fg)]">
        <Loader2 className="animate-spin" />
      </div>
    );
  }
  if (!session) return <AuthPage />;

  if (role !== "admin") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center bg-[var(--color-bg)] text-[var(--color-fg)]">
        <ShieldAlert size={40} className="text-amber-500" />
        <p className="font-semibold">管理者権限がありません</p>
        <p className="text-sm text-ink-soft dark:text-white/70">
          このアカウント（{session.user.email}）には管理者権限が付与されていません。
        </p>
        <button onClick={() => supabase.auth.signOut()} className="px-4 py-2 rounded-lg border border-[var(--color-border)] text-sm">
          ログアウト
        </button>
      </div>
    );
  }

  const current = NAV_ITEMS.find((n) => n.k === tab);

  return (
    <div className="min-h-screen flex bg-gray-50 dark:bg-[var(--color-bg)] text-[var(--color-fg)]">
      {/* 旧デモUI: ダークサイドナビ（PC） */}
      <aside className="hidden md:flex w-56 bg-slate-900 text-white flex-col flex-shrink-0 sticky top-0 h-screen">
        <div className="p-5 border-b border-white/10">
          <span className="inline-flex bg-white rounded-lg px-2.5 py-1.5">
            <img src="/brand/sirasu-logo.png?v=4" alt="SitaMe" className="h-6 w-auto" />
          </span>
          <div className="text-brand-300 text-xs font-bold tracking-wider mt-3">ADMIN</div>
          <div className="text-sm font-black mt-0.5">シタミー運営</div>
          <div className="text-xs text-slate-400 mt-1">合同会社支良洲工業</div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV_ITEMS.map((it) => (
            <button
              key={it.k}
              onClick={() => setTab(it.k)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition ${tab === it.k ? "bg-brand-500 text-white" : "text-slate-300 hover:bg-white/5"}`}
            >
              {it.icon}
              {it.t}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10 text-xs text-slate-400 space-y-2">
          <div className="truncate">ログイン中：{session.user.email}</div>
          <div className="flex gap-2">
            <button aria-label="テーマ切替" onClick={() => setMode(mode === "dark" ? "light" : "dark")} className="p-2 rounded hover:bg-white/10">
              {mode === "dark" ? <Sun size={14} /> : <Moon size={14} />}
            </button>
            <button aria-label="ログアウト" onClick={() => supabase.auth.signOut()} className="p-2 rounded hover:bg-white/10">
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* モバイル用ヘッダー */}
        <header className="md:hidden safe-pt sticky top-0 z-10 bg-white dark:bg-white/5 border-b border-[var(--color-border)] flex items-center justify-between px-4 py-3">
          <span className="font-bold text-sm">シタミー運営{current ? ` — ${current.t}` : ""}</span>
          <div className="flex items-center gap-1">
            <button aria-label="テーマ切替" onClick={() => setMode(mode === "dark" ? "light" : "dark")} className="p-2 rounded hover:bg-brand-50 dark:hover:bg-brand-800/30">
              {mode === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <button aria-label="ログアウト" onClick={() => supabase.auth.signOut()} className="p-2 rounded hover:bg-brand-50 dark:hover:bg-brand-800/30">
              <LogOut size={16} />
            </button>
          </div>
        </header>

        {/* PC用コンテンツヘッダー */}
        <div className="hidden md:block px-8 pt-6">
          <h1 className="text-xl font-black">{current?.t}</h1>
        </div>

        <main className="flex-1 overflow-y-auto pb-16 md:pb-6 md:px-4">
          {tab === "dash" && <DashboardPage />}
          {tab === "vendors" && <VendorsPage />}
          {tab === "cases" && <CasesPage />}
          {tab === "orders" && <OrdersPage />}
          {tab === "makers" && <MakersPage />}
          {tab === "payouts" && <PayoutsPage />}
          {tab === "accounts" && <AccountsPage />}
          {tab === "ai" && <AiLearningPage />}
        </main>

        {/* モバイル用下部ナビ */}
        <nav className="md:hidden safe-pb fixed bottom-0 inset-x-0 bg-white dark:bg-[var(--color-bg)] border-t border-[var(--color-border)] grid grid-cols-8 z-20">
          {NAV_ITEMS.map((it) => (
            <button
              key={it.k}
              onClick={() => setTab(it.k)}
              className={`flex flex-col items-center gap-0.5 py-2 ${tab === it.k ? "text-brand-600 dark:text-brand-300" : "text-gray-400"}`}
            >
              {it.icon}
              <span className="text-[10px] font-bold">{it.t}</span>
            </button>
          ))}
        </nav>
      </div>
    </div>
  );
}
